import { LoadingDots, Tooltip } from '@loadsmart/loadsmart-ui'
import { Layout, Text, type TextProps, type StackProps } from '@loadsmart/miranda-react'
import { toCSSValue } from '@loadsmart/miranda-react/dist/tokens'

import { RequestStatus } from 'core/domain/Request'
import { Icon } from 'core/ui/components/icon/Icon'

import type { ShipmentAccountingSummary as ShipmentAccountingSummaryType } from '../domain/ShipmentCharges'
import { formatChargeCurrency } from '../presenters/shipmentChargePresenter'
import { ResponsiveBox, ResponsiveGroup } from './ShipmentAccountingSummary.styles'

type SummaryItemProps = {
  label: string
  total?: number
  totalColor?: TextProps['color']
  totalVariant?: TextProps['variant']
  gap?: StackProps['gap']
  tooltip?: string
  isLoading: boolean
}

function SummaryItem({
  label,
  total,
  totalColor = 'color-text-primary',
  totalVariant = 'number-md',
  gap = 'spacing-2',
  tooltip,
  isLoading,
}: SummaryItemProps) {
  return (
    <Layout.Stack gap={gap} align="flex-end">
      <Layout.Group gap="spacing-0-5" align="center">
        <Text variant="body-sm" color="color-text-primary">
          {label}
        </Text>
        {tooltip && (
          <Tooltip message={tooltip}>
            <Icon name="question-circle" color="neutral" size={12} />
          </Tooltip>
        )}
      </Layout.Group>
      {isLoading ? (
        <span style={{ padding: toCSSValue('spacing-1') }}>
          <LoadingDots size={5} />
        </span>
      ) : (
        <Text variant={totalVariant} color={totalColor} aria-label={label}>
          {formatChargeCurrency(total)}
        </Text>
      )}
    </Layout.Stack>
  )
}

type ShipmentAccountingSummaryProps = {
  summary: ShipmentAccountingSummaryType | undefined
  summaryStatus: RequestStatus
}

export function ShipmentAccountingSummary({
  summary,
  summaryStatus,
}: ShipmentAccountingSummaryProps) {
  const isLoading = summaryStatus === RequestStatus.Pending

  if (summaryStatus === RequestStatus.Error || (summaryStatus === RequestStatus.Done && !summary)) {
    return null
  }

  return (
    <ResponsiveBox
      backgroundColor="color-background-primary"
      borderRadius="border-radius-s"
      borderWidth="border-thin"
      borderColor="color-border"
    >
      <Text variant="body-lg-bold" color="color-text-tertiary">
        Summary
      </Text>
      <ResponsiveGroup justify="flex-end">
        <ResponsiveGroup>
          <SummaryItem
            label="Quoted rate"
            total={summary?.quotedTotal}
            tooltip="Haul price"
            isLoading={isLoading}
          />
          <SummaryItem
            label="Pending charges"
            total={summary?.pendingCharges}
            totalColor="color-warning-100"
            isLoading={isLoading}
          />
          <SummaryItem
            label="Approved charges"
            total={summary?.approvedCharges}
            isLoading={isLoading}
          />
        </ResponsiveGroup>
        <SummaryItem
          label="Approved total"
          total={summary?.total}
          totalVariant="heading-md-bold"
          totalColor="color-text-highlight"
          gap="spacing-1"
          isLoading={isLoading}
        />
      </ResponsiveGroup>
    </ResponsiveBox>
  )
}
